import {
	ChangeDetectionStrategy,
	Component,
	computed,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { ClearingService, SeoMetadataService } from '@uc/utilities';
import {
	CourseSearchBarComponent,
	MainHeaderComponent,
	PrimaryBlueBtnComponent,
	StaticCardComponent,
} from '@web/shared/ui';
import { HeadingComponent } from '@uc/shared/ui';
import {
	CLEARING_CARD,
	DEGREE_LEVEL_OPTIONS,
	DIRECTORY_CARD,
	HEADER_DATA,
	INFO_SECTION,
	METADATA,
	POSTGRADUATE_CARD,
	PRIORITISED_DEGREES_LIST,
	UNDERGRADUATE_CARD,
} from '../../static/courses-hub.properties';
import { CoursesHubLinksComponent } from '../../components/courses-hub-links/courses-hub-links.component';
import { getDegreeLevelOptions } from '../../utils/utils';
import { HECoSSubjectValidatorService } from '@uc/web/core';
import { getCourseSearchSegments } from '../../utils/route.utils';
import { CourseSearchDataService } from '../../data-access/course-search-data.service';
import { DegreeSubjectCategoryComponent } from '../../components/degree-subject-category/degree-subject-category.component';
import { NgClass } from '@angular/common';
import { CLEARING_ENABLED } from '@uc/web/config';

@Component({
	selector: 'uc-courses-hub',
	templateUrl: './courses-hub.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		HeadingComponent,
		MainHeaderComponent,
		CourseSearchBarComponent,
		StaticCardComponent,
		CoursesHubLinksComponent,
		PrimaryBlueBtnComponent,
		DegreeSubjectCategoryComponent,
		NgClass,
	],
})
export class CoursesHubComponent implements OnInit, OnDestroy {
	headerData = { ...HEADER_DATA };
	infoSection = { ...INFO_SECTION };
	undergraduateCard = { ...UNDERGRADUATE_CARD };
	postgraduateCard = { ...POSTGRADUATE_CARD };
	clearingCard = { ...CLEARING_CARD };
	directoryCard = { ...DIRECTORY_CARD };
	degreeLevelOptions = getDegreeLevelOptions(['Undergraduate', 'Postgraduate']);
	clearingEnabled = CLEARING_ENABLED;

	degreeLevelHubLinks = computed(() => {
		const links = this._clearingSearchDataSrv.degreeLevelHubLinks();
		const toRemove = new Set([
			'/courses',
			'/courses/clearing',
			'/courses/degrees/undergraduate',
			'/courses/degrees/postgraduate',
		]);
		if (!links) return;

		return links.filter((link) => !toRemove.has(link.link));
	});
	topDegreeSubjects = [...PRIORITISED_DEGREES_LIST];

	constructor(
		private _router: Router,
		private _seoSrv: SeoMetadataService,
		private _clearingSrv: ClearingService,
		private _clearingSearchDataSrv: CourseSearchDataService,
		private _hecosSubjectValidatorSrv: HECoSSubjectValidatorService,
	) {}

	ngOnInit(): void {
		this._clearingSearchDataSrv.fetchDegreeLevelHubLinks('DEGREE');

		this._seoSrv.updateTitle(METADATA.title);
		this._seoSrv.updateDescription(METADATA.description);
		this._updateClearingCTACopy();

		if (this.clearingEnabled) {
			const clearing = DEGREE_LEVEL_OPTIONS.find(
				(option) => option.label === 'Clearing',
			);
			const isClearingPresent = this.degreeLevelOptions.some(
				(option) => option.label === 'Clearing',
			);
			if (clearing && !isClearingPresent) {
				this.degreeLevelOptions.unshift(clearing);
			}
		}
	}

	ngOnDestroy(): void {
		this._clearingSearchDataSrv.degreeLevelHubLinks.set(null);
		this._clearingSearchDataSrv.topDegreeSubjects.set(null);
	}

	async onSearch(autosuggest: { searchTerm: string; degree?: string }) {
		if (!autosuggest.searchTerm) return;

		const { searchTerm, degree } = autosuggest;

		const subject = searchTerm.replace(/ /g, '-').toLowerCase();
		const isHECoSSubject =
			await this._hecosSubjectValidatorSrv.isHECoSSubject(subject);

		const segments = getCourseSearchSegments(isHECoSSubject, degree, subject);

		this._router.navigate([...segments], {
			queryParams: isHECoSSubject ? {} : { search: subject },
		});
	}

	private _updateClearingCTACopy() {
		const clearingYear = this._clearingSrv.getYear().toString();
		const isClearing = this._clearingSrv.isClearing();

		const clearingStatusMessage = isClearing
			? 'Clearing is open! '
			: `Clearing opens July ${clearingYear}.`;

		const description = this.clearingCard.desc.replace(
			'[CLEARING_STATUS]',
			clearingStatusMessage,
		);

		this.clearingCard.desc = description;
	}
}
